<template>
  <section v-if="smallTile && showVoucher" class="certification-list-item__wrapper">
    <div
      @click="updateSelectedCertification(certification)"
      class="certification-list-item certification-list-item--grid certification-list-item--small-tile"
    >
      <div class="certification-list-item__badge-wrapper">
        <img
          :src="certification.icon"
          :alt="certification.name"
          :class="[
            'certification-list-item__badge-img',
            'certification-list-item__badge-img--small-tile',
            `certification-list-item__badge--${certification.merchant_id}`,
          ]"
        />
      </div>
      <h4
        class="certification-list-item__name certification-list-item__name--small-tile"
        @click.stop
      >
        {{ certification.name }}
        <v-popover
          popoverInnerClass="certification-list-item__description-popover-inner"
          popoverArrowClass="certification-list-item__description-popover-arrow"
          placement="top"
          trigger="click"
          :open="isDescriptionPopoverOpen"
        >
          <NotificationIcon
            class="certification-list-item__description-popover"
            @click="toggleDescriptionPopoverOpen"
          />
          <template slot="popover">
            <div class="certification-list-item__description-popover-wrapper">
              <div
                class="certification-list-item__description-popover-close-x-btn"
                @click="toggleDescriptionPopoverOpen"
              />
              <div class="certification-list-item__description-popover-content">
                <div class="certification-list-item__description-popover-title">
                  {{ certification.name }}
                </div>
                <div class="certification-list-item__description-popover-description">
                  {{ certification.description }}
                </div>
              </div>
              <button
                class="btn btn--primary btn--small certification-list-item__description-popover-close-btn"
                @click="toggleDescriptionPopoverOpen"
              >
                Close
              </button>
            </div>
          </template>
        </v-popover>
      </h4>
      <CertificationListItemPrice
        :certification="certification"
        :showDiscountPrice="showDiscountPrice"
        small-tile
      />
      <div
        class="certification-list-item__check-box certification-list-item__check-box--small-tile"
      >
        <CheckBox :isSelected="isSelected" />
      </div>
    </div>
  </section>
  <section v-else-if="showVoucher" class="certification-list-item__wrapper">
    <div
      @click="updateSelectedCertification(certification)"
      class="certification-list-item certification-list-item--grid"
    >
      <div class="certification-list-item__badge-wrapper">
        <img
          :src="certification.icon"
          :alt="certification.name"
          :class="[
            'certification-list-item__badge-img',
            `certification-list-item__badge--${certification.merchant_id}`,
          ]"
        />
      </div>
      <h4 class="certification-list-item__name">{{ certification.name }}</h4>
      <p class="certification-list-item__description">
        {{ certification.description }}
      </p>
      <CertificationListItemPrice
        :certification="certification"
        :showDiscountBadge="showDiscountBadge"
        :showDiscountPrice="showDiscountPrice"
      />
      <div class="certification-list-item__check-box">
        <CheckBox :isSelected="isSelected" />
      </div>
    </div>
  </section>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import CONSTANTS from '@/constants'
import CheckBox from '@/components/shared/CheckBox.vue'
import CertificationListItemPrice from '@/components/certifications/CertificationListItemPrice.vue'
import NotificationIcon from '@/assets/icons/notification.svg'
import { updateCart, priceDetailUpdates } from '@/helpers/mixins'

export default {
  name: 'CertificationListItem',
  mixins: [updateCart, priceDetailUpdates],
  components: {
    CheckBox,
    CertificationListItemPrice,
    NotificationIcon: { ...NotificationIcon, name: 'NotificationIcon' },
  },
  data() {
    return {
      isDescriptionPopoverOpen: false,
    }
  },
  props: {
    certification: {
      type: Object,
      required: true,
    },
    smallTile: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  computed: {
    ...mapState({
      usersCurrentSubscriptions: state => state.subscriptions.usersCurrentSubscriptions,
      userHasSubscription: state => state.subscriptions.userHasSubscription,
    }),
    ...mapGetters({
      isBusinessUser: 'business/isBusinessUser',
      isBusinessPlanExpired: 'business/isBusinessPlanExpired',
      isBlackFridayActive: 'featureFlags/isBlackFridayActive',
      isLeapYearPromoActive: 'featureFlags/isLeapYearPromoActive',
    }),
    userHasFundamentalsSubscription() {
      const { PLANS } = CONSTANTS
      const userHasFundamentals = !!this.usersCurrentSubscriptions.find(subscription =>
        [PLANS.MONTHLY, PLANS.ANNUAL].includes(subscription.plan_code)
      )
      return this.usersCurrentSubscriptions.length > 0 && userHasFundamentals
    },
    showVoucher() {
      const {
        VOUCHERS: { EJPT, EDA, EWPT },
      } = CONSTANTS
      if (this.certification.merchant_id === EDA && this.userHasFundamentalsSubscription) {
        return false
      }
      if ([EJPT, EDA, EWPT].includes(this.certification.merchant_id)) {
        return (
          this.usersCurrentSubscriptions.length > 0 ||
          (this.isBusinessUser && !this.isBusinessPlanExpired)
        )
      }
      return true
    },
    isSelected() {
      return !!this.shoppingCart.find(item => item.merchant_id === this.certification.merchant_id)
    },
    isPromotionActive() {
      return this.isBlackFridayActive || this.isLeapYearPromoActive
    },
    showDiscountBadge() {
      return this.isPromotionActive
    },
    showDiscountPrice() {
      return this.isPromotionActive
    },
  },
  methods: {
    toggleDescriptionPopoverOpen() {
      this.isDescriptionPopoverOpen = !this.isDescriptionPopoverOpen
    },
  },
}
</script>

<style lang="scss" scoped>
.certification-list-item {
  $this: &;
  padding: 40px;
  background: var(--mid-gray-gradient);
  border-radius: 10px;
  color: var(--white);
  font-family: var(--font-regular);
  cursor: pointer;
  display: grid;
  margin-bottom: 20px;
  transition: all 1s ease-out;
  height: 100%;
  @include mobile {
    grid-template-columns: 20% 55% 20% 5%;
  }
  @include desktop {
    grid-template-columns: 15% 67% 15% 3%;
  }
  &:hover {
    background: var(--mid-gray-translucid-gradient);
  }
  &:active {
    transition: all 0.5s ease-out;
    background: var(--charcoal-gradient-two);
  }
  &__badge {
    &-wrapper {
      position: relative;
      grid-row: 1/2;
      @include mobile {
        grid-area: 1/1/3/2;
      }
    }
    &-img {
      width: 123px;
    }
  }
  &__name {
    margin: 0;
    margin-top: 20px;
    font-weight: 700;
    grid-area: 1/2/1/3;
    font-size: rem(20px);
    line-height: rem(26px);
    @include mobile {
      margin-top: 30px;
      font-size: rem(24px);
      line-height: rem(32px);
    }
    &--small-tile {
      margin-top: 0;
      font-size: rem(18px);
      font-style: normal;
      font-weight: 500;
      line-height: rem(24px);
      display: flex;
      gap: 5px;
    }
  }
  &__description {
    font-style: normal;
    font-weight: 450;
    font-size: 14px;
    line-height: 20px;
    margin: 0;
    margin-top: 10px;
    grid-area: 2/1/2/4;
    @include mobile {
      padding-right: 0;
    }
    &-popover {
      width: 16px;
      min-width: 16px;
      margin-right: 10px;
      stroke: var(--light-blue-gray);
      &:hover {
        stroke: var(--white);
      }
    }
  }
  &__check-box {
    grid-area: 1/3/2/4;
    display: flex;
    justify-content: flex-end;
    align-items: flex-start;
    margin-top: 30px;
    &--small-tile {
      align-items: center;
      margin-top: 0;
    }
  }
  &__badge-wrapper {
    grid-area: 1;
  }
  &__badge-img {
    width: 100px;
    @include mobile {
      width: 110px;
    }
    &--small-tile {
      width: 80px;
      position: absolute;
      top: -25px;
    }
  }
  &--grid {
    margin-bottom: 0;
    grid-template-rows: 167px auto;
    grid-template-columns: 43% 47% 10%;
    overflow: auto;
    @include mobile {
      grid-template-columns: 45% 45% 10%;
    }
    @include tablet-ipad-pro {
      grid-template-columns: 35% 55% 10%;
    }
    @include desktop {
      grid-template-columns: 30% 60% 10%;
    }
  }
  &--small-tile {
    padding: 13px 20px;
    height: 80px;
    overflow: visible;
    gap: 20px;
    grid-template-rows: 55px;
    grid-template-columns: 80px auto 15%;
  }
}
</style>
<style lang="scss">
.certification-list-item__description-popover {
  &-inner {
    color: var(--white);
    background: var(--mid-gray-gradient);
    padding: 24px;
    border-radius: 5px;
    box-shadow: 0 5px 30px rgba(black, 0.1);
  }
  &-arrow {
    visibility: hidden;
  }
  &-wrapper {
    width: 100%;
    max-width: 400px;
    display: flex;
    flex-flow: column;
  }
  &-title {
    font-size: rem(18px);
    font-style: normal;
    font-weight: 500;
    line-height: rem(24px);
  }
  &-description {
    color: var(--light-blue-gray);
    font-size: rem(14px);
    font-style: normal;
    font-weight: 450;
    line-height: rem(20px);
  }
  &-content {
    margin-bottom: 20px;
  }
  &-close {
    &-x-btn {
      align-self: flex-end;
      cursor: pointer;
      width: 16px;
      height: 24px;
      // @/assets/icons/close_modal_x.svg
      background-image: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M18 6L6 18' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M6 6L18 18' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A");
      background-size: 18px 24px;
      background-repeat: no-repeat;
    }
    &-btn {
      width: 70px;
      align-self: flex-end;
    }
  }
}
</style>
